import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { useServiceWithAuth } from '@/hooks/useServiceWithAuth.ts';
import { useGlobalNotifications } from '@/hooks/system/useGlobalNotifications.ts';
import { TransferPatientsService, type TransferPatient } from '@/services/features/TransferPatientsService.ts';
import { NotificationsService, type TransferPatient as NotificationTransferPatient } from '@/services/features/NotificationsService.ts';

export const useTransferPatientsData = () => {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const [acceptingId, setAcceptingId] = useState<number | null>(null);

  // Get service instances with proper auth context
  const transferPatientsService = useServiceWithAuth(TransferPatientsService);

  // Use global notifications for real-time updates instead of manual fetching
  const { notifications, isLoading, notificationCount } = useGlobalNotifications();
  
  console.log('📊 useTransferPatientsData - Global notifications state:', { 
    notificationsCount: notifications.length,
    isLoading, 
    notificationCount,
    user: user?.type,
    userId: user?.id 
  });

  // Sort notifications: null assigned_to_user first, then others
  const sortedNotifications = [...notifications].sort((a, b) => {
    if (a.assigned_to_user === null && b.assigned_to_user !== null) return -1;
    if (a.assigned_to_user !== null && b.assigned_to_user === null) return 1;
    return 0;
  });

  const handleAcceptTransfer = async (patient: NotificationTransferPatient) => {
    console.log('🚀 handleAcceptTransfer called with patient:', patient);
    console.log('🔐 Auth context:', { token: !!token, user: !!user, userId: user?.id });
    console.log('🔧 Services:', {
      transferPatientsService: !!transferPatientsService,
      transferServiceContext: transferPatientsService?.context
    });

    if (!token || !user) {
      console.error('❌ Missing token or user');
      return;
    }

    // Try using the main ID first, then fallback to enrollment_patient_id
    const patientId = patient.id || patient.enrollment_patient_id;

    if (!patientId) {
      console.error('❌ No valid patient ID found');
      toast({
        title: "Error",
        description: "No valid patient ID found",
        variant: "destructive"
      });
      return;
    }

    console.log('✅ useTransferPatientsData - attempting to accept transfer for patient:', {
      id: patient.id,
      enrollment_patient_id: patient.enrollment_patient_id,
      patient_name: patient.patient_name,
      using_id: patientId
    });

    setAcceptingId(patientId);

    try {
      // Convert NotificationTransferPatient to TransferPatient format
      const transferPatient: TransferPatient = {
        id: patient.id,
        // Map transfer_id from notification view (pep_vw_patient_user_transfer)
        transfer_id: patient.transfer_id,
        // Map enrollment_patient_id from notification view into service's expected field
        pep_patient_clinic_data_id: patient.enrollment_patient_id,
        patient_name: patient.patient_name,
        // Map assigned_to_user into service's expected field
        assigned_cs_id: patient.assigned_to_user
      };

      console.log('🔄 Calling transferPatientsService.acceptTransferWithRetry...');
      const success = await transferPatientsService.acceptTransferWithRetry(transferPatient, parseInt(user.id));
      console.log('📋 acceptTransferWithRetry result:', success);

      if (success) {
        console.log('✅ useTransferPatientsData - transfer accepted successfully');
        toast({
          title: "Success",
          description: "Patient transfer accepted successfully",
          variant: "default",
          duration: 7000
        });
        // Data will auto-update via global WebSocket notifications
        // No manual refetch needed
      } else {
        console.error('❌ Transfer acceptance returned false');
        toast({
          title: "Error",
          description: "Failed to accept patient transfer",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('❌ useTransferPatientsData - error accepting transfer:', error);
      toast({
        title: "Error",
        description: `Failed to accept patient transfer: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      setAcceptingId(null);
    }
  };

  return {
    sortedNotifications,
    isLoading,
    notificationCount,
    acceptingId,
    handleAcceptTransfer
  };
};